
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Star, Pin, MoreVertical, Plus, Trash, Lightbulb } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useSecureAdmin } from "@/hooks/useSecureAdmin";

type Entry = {
  id: string;
  is_favorite: boolean;
  is_pinned?: boolean;
  is_postscript?: boolean;
};

type EntryCardActionsProps = {
  entry: Entry;
  currentUserId?: string;
  isOwner: boolean;
  isSharedEntryState: boolean;
  toggleFavorite: (entry: Entry) => void;
  togglePinned: (entry: Entry) => void;
  copyShareLink: (entry: Entry) => void;
  addToPersonalDashboard?: (entry: Entry) => void;
  onRemove?: (entryId: string) => void;
  handleRemoveShared: () => void;
};

export const EntryCardActions: React.FC<EntryCardActionsProps> = ({
  entry,
  currentUserId,
  isOwner,
  isSharedEntryState,
  toggleFavorite,
  togglePinned,
  copyShareLink,
  addToPersonalDashboard,
  onRemove,
  handleRemoveShared
}) => {
  const { isAdmin } = useSecureAdmin();

  const togglePostscript = async () => {
    try {
      const { error } = await supabase
        .from("entries")
        .update({ is_postscript: !entry.is_postscript })
        .eq("id", entry.id);

      if (error) throw error;
      
      toast.success(
        entry.is_postscript 
          ? "Removed from Postscript" 
          : "Added to Postscript"
      );
      
      window.location.reload();
    } catch (error) {
      console.error("Error toggling postscript:", error);
      toast.error("Failed to update Postscript status");
    }
  };

  return (
    <div className="flex items-center gap-1">
      {isOwner && !isSharedEntryState && (
        <>
          {entry.is_favorite && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => toggleFavorite(entry)}
            >
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="sr-only">Remove from favorites</span>
            </Button>
          )}
          
          {entry.is_pinned && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => togglePinned(entry)}
            >
              <Pin className="h-4 w-4" />
              <span className="sr-only">Unpin from dashboard</span>
            </Button>
          )}
          
          {entry.is_postscript && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => togglePostscript()}
            >
              <Lightbulb className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              <span className="sr-only">Remove from Postscript</span>
            </Button>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">More options</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => toggleFavorite(entry)}>
                {entry.is_favorite ? "Remove from favorites" : "Add to favorites"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => togglePinned(entry)}>
                {entry.is_pinned ? "Unpin from dashboard" : "Pin to dashboard"}
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={togglePostscript}>
                  {entry.is_postscript ? "Remove from Postscript" : "Add to Postscript"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild>
                <Link to={`/dashboard/edit/${entry.id}`}>Edit entry</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => copyShareLink(entry)}>
                Share entry
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
      
      {isOwner && isSharedEntryState && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-red-500 hover:text-red-700"
          onClick={handleRemoveShared}
          title="Remove shared entry"
        >
          <Trash className="h-4 w-4" />
          <span className="sr-only">Remove shared entry</span>
        </Button>
      )}
      
      {!isOwner && addToPersonalDashboard && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => addToPersonalDashboard(entry)}
          title="Add to your dashboard"
        >
          <Plus className="h-4 w-4" />
          <span className="sr-only">Add to your notes</span>
        </Button>
      )}
    </div>
  );
};
